import type { Customer, Task } from "@api/types";
import { ApiError, handleApiError } from "@api/utils";
import { useAuthStore } from "@lib/auth";
import { formatDate, getCustomerName } from "@lib/utils";
import useMatchMutate from "@ui/hooks/match-mutate";
import { CalendarClock, MessageSquare, SearchIcon, UserPlus, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useCustomers } from "@/api/customer";
import { useOrgMembers } from "@/api/org-member";
import { taskService } from "@/api/task";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TagField } from "@/components/ui/form/tag-field";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TiptapEditor } from "@/components/ui/tiptap-editor";
import { useDebounce } from "@/hooks/use-debounce";
import { CustomerAvatar } from "@/modules/customer/customer-avatar";
import { trackTaskCreated } from "./analytics";
import { AssigneePicker } from "./assignee-picker";
import { DueDatePicker } from "./due-date-picker";
import { descriptionSnippet, getUserName, TaskUserAvatar } from "./shared";

interface CreateTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gym: string;
  /** Prefill when creating from a contact page or a conversation */
  defaults?: { customer?: Customer; thread?: string };
  onCreated?: (task: Task) => void;
}

function ContactPicker({
  gym,
  value,
  onChange,
}: {
  gym: string;
  value: Customer | null;
  onChange: (customer: Customer | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim(), 300);
  const { data: customers, isLoading } = useCustomers(
    { gym, query: debouncedSearch, limit: 8 },
    { skip: !open || !debouncedSearch, keepPreviousData: true },
  );

  if (value) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1.5 shadow-none"
        onClick={() => onChange(null)}
      >
        <CustomerAvatar customer={value} className="size-4 border-0 text-[9px]" />
        {getCustomerName(value)}
        <X className="size-3 text-muted-foreground" />
      </Button>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="gap-1.5 shadow-none">
          <UserPlus className="size-3.5 text-muted-foreground" />
          Contact
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-2" align="start">
        <Input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search contacts..."
          leadingIcon={<SearchIcon className="size-3.5 text-muted-foreground" />}
          className="text-sm"
        />
        <div className="mt-2 max-h-56 overflow-y-auto">
          {!debouncedSearch ? (
            <p className="px-1 py-3 text-center text-sm text-muted-foreground">
              Search for a contact.
            </p>
          ) : customers?.length ? (
            <ul className="divide-y divide-border/50">
              {customers.map((customer) => (
                <li key={customer.id}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-muted/50"
                    onClick={() => {
                      onChange(customer);
                      setOpen(false);
                      setSearch("");
                    }}
                  >
                    <CustomerAvatar customer={customer} className="size-6" />
                    <span className="truncate">{getCustomerName(customer)}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-1 py-3 text-center text-sm text-muted-foreground">
              {isLoading ? "Searching..." : "No contacts found."}
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function CreateTaskDialog({
  open,
  onOpenChange,
  gym,
  defaults,
  onCreated,
}: CreateTaskDialogProps) {
  const org = useAuthStore((state) => state.org);
  const matchMutate = useMatchMutate();
  const { data: members } = useOrgMembers(org?.id ?? "", undefined, { skip: !org?.id });

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [customer, setCustomer] = useState<Customer | null>(defaults?.customer ?? null);
  const [assignee, setAssignee] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [tags, setTags] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset transient state whenever the dialog closes.
  useEffect(() => {
    if (!open) {
      setTitle("");
      setDescription("");
      setCustomer(defaults?.customer ?? null);
      setAssignee(null);
      setDueDate(null);
      setTags("");
      setBusy(false);
    }
  }, [open, defaults?.customer]);

  const assigneeMember = members?.find((member) => member.user.id === assignee);

  async function handleSubmit() {
    if (!title.trim() || busy) return;
    try {
      setBusy(true);
      const task = await taskService.create({
        gym,
        title: title.trim(),
        // Rich text: only send when there's real content (an "empty" tiptap
        // doc still serializes to "<p></p>").
        description: descriptionSnippet(description) ? description : undefined,
        customer: customer?.id,
        thread: defaults?.thread,
        user: assignee ?? undefined,
        dueDate: dueDate ?? undefined,
        tags: tags ? tags.split("|").filter(Boolean) : undefined,
      });
      await matchMutate(/\/tasks/);
      trackTaskCreated({
        gym,
        source: defaults?.thread ? "conversation" : defaults?.customer ? "contact" : "tasks-page",
        assigned: !!assignee,
        hasCustomer: !!customer,
        hasDueDate: !!dueDate,
        hasDescription: !!descriptionSnippet(description),
        tags: tags ? tags.split("|").filter(Boolean).length : 0,
      });
      toast.success("Task created");
      onOpenChange(false);
      onCreated?.(task);
    } catch (err) {
      if (err instanceof ApiError) handleApiError(err);
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>New task</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
          className="flex flex-col gap-1"
        >
          <input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Task title"
            maxLength={500}
            className="w-full border-0 bg-transparent p-0 text-lg font-medium text-foreground outline-none placeholder:text-muted-foreground focus:ring-0"
          />
          <TiptapEditor
            content={description}
            onUpdate={setDescription}
            placeholder="Add a description..."
            editorClassName="min-h-[72px]"
            className="mt-1"
          />

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <ContactPicker gym={gym} value={customer} onChange={setCustomer} />
            {defaults?.thread ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="pointer-events-none gap-1.5 shadow-none"
              >
                <MessageSquare className="size-3.5 text-muted-foreground" />
                Conversation
              </Button>
            ) : null}
            <AssigneePicker value={assignee} members={members} onChange={setAssignee}>
              <Button type="button" variant="outline" size="sm" className="gap-1.5 shadow-none">
                {assigneeMember ? (
                  <>
                    <TaskUserAvatar user={assigneeMember.user} className="size-4 border-0" />
                    {getUserName(assigneeMember.user)}
                  </>
                ) : (
                  <>
                    <UserPlus className="size-3.5 text-muted-foreground" />
                    Assignee
                  </>
                )}
              </Button>
            </AssigneePicker>
            <DueDatePicker
              value={dueDate}
              onChange={setDueDate}
              align="start"
              trigger={
                <Button type="button" variant="outline" size="sm" className="gap-1.5 shadow-none">
                  <CalendarClock className="size-3.5 text-muted-foreground" />
                  {dueDate ? formatDate(dueDate, "MMM d, h:mm a") : "Due date"}
                </Button>
              }
            />
          </div>

          <div className="mt-3">
            <TagField
              name="tags"
              label="Tags"
              placeholder="Add tags..."
              value={tags}
              onValueChange={setTags}
            />
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!title.trim()} loading={busy} loadingText="Creating...">
              Create task
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
